import { shd } from '@shd/jslib/models';

import type {
  GapType as GrommetGapType,
  EdgeSizeType as GrommetEdgeSizeType,
} from 'grommet/utils';

/**
 *   App
 * ------------------------------------------------------ */
export type AuthProviders =
  | 'apple'
  | 'email'
  | 'facebook'
  | 'github'
  | 'google'
  | 'phone';

export type SportType =
  | 'basketball'
  | 'baseball'
  | 'hockey'
  | 'lacrosse'
  | 'soccer'
  | 'softball'
  | 'volleyball'
  | 'water_polo';

export type TeamImportPartner = 'aes' | 'pg' | 'teamsnap' | 'usssa';
export type TeamImportStep = 'manual' | TeamImportPartner;

export interface DebounceSettings {
  leading?: boolean | undefined;
  maxWait?: number | undefined;
  trailing?: boolean | undefined;
}

export type Meridiem = 'AM' | 'PM';

/**
 *   SBOT (v2) Theme
 * ------------------------------------------------------ */
// Colors
export type RGBColor = `rgb(${number}, ${number}, ${number})`;
export type RGBAColor = `rgba(${number}, ${number}, ${number}, ${number})`;
export type HexColor = `#${string}`;
export type ColorType = RGBColor | RGBAColor | HexColor;
export interface ColorPalette {
  [key: string]: string;
}

export interface ColorPaletteSettings {
  name: string;
  baseColor: ColorType;
}

// Sizing
export type BasicTShirtSizing = 'small' | 'medium' | 'large';

export interface SizePalette {
  [key: number]: string;
}

export interface SizePaletteSettings {
  min: number;
  max: number;
  steps: number;
}

export type SizeUnits = `${number}px` | `${number}rem`;
export type TShirtSizing =
  | BasicTShirtSizing
  | 'none'
  | 'hair'
  | 'xxsmall'
  | 'xxsmall'
  | 'xsmall'
  | 'xlarge'
  | 'xxlarge'
  | 'xxxlarge';

// Typography
export type HeadlineStyles =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'subhead'
  | 'subhead-bold'
  | 'subhead-semibold'
  | 'subhead-all-caps';

export type TextStyles =
  | 'subhead'
  | 'subhead-bold'
  | 'subhead-semibold'
  | 'subhead-all-caps'
  | 'body'
  | 'body-semibold'
  | 'footnote'
  | 'footnote-semibold'
  | 'footnote-bold'
  | 'legal'
  | 'score';

/**
 *   SBOT (v2) Grommet
 * ------------------------------------------------------ */
export type BreakpointSizeType = 'small' | 'medium' | 'large' | 'xlarge';
export type BreakpointWidthType =
  | BreakpointSizeType
  | '100%'
  | number
  | {
      width?: BreakpointSizeType | '100%' | number;
      max?: BreakpointSizeType | '100%' | number;
      min?: BreakpointSizeType | '100%' | number;
    };
export type EdgeSizeType =
  | 'none'
  | GrommetEdgeSizeType
  | {
      bottom?: GrommetEdgeSizeType | string | number;
      end?: GrommetEdgeSizeType | string | number;
      horizontal?: GrommetEdgeSizeType | string | number;
      left?: GrommetEdgeSizeType | string | number;
      right?: GrommetEdgeSizeType | string | number;
      start?: GrommetEdgeSizeType | string | number;
      top?: GrommetEdgeSizeType | string | number;
      vertical?: GrommetEdgeSizeType | string | number;
    }
  | string
  | number;
export type GapProp = GrommetGapType | number;
export type GapType = GapProp | { row?: GapProp; column?: GapProp };
export type GradientDirection = 'top' | 'right' | 'bottom' | 'left';
export type LinearGradientDirection = GradientDirection | `${number}deg`;
export type RadialGradientDirection = GradientDirection | 'center';
export type RadialGradientShape = 'ellipse' | 'circle';

export type RoundType =
  | boolean
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'full'
  | string
  | number
  | {
      corner?:
        | 'top'
        | 'left'
        | 'bottom'
        | 'right'
        | 'top-left'
        | 'top-right'
        | 'bottom-left'
        | 'bottom-right';
      size?:
        | 'xsmall'
        | 'small'
        | 'medium'
        | 'large'
        | 'xlarge'
        | string
        | number;
    };

/**
 *   Basic
 * ------------------------------------------------------ */
export type Dict = {
  [key: string]: unknown;
};

export interface ErrorMessage {
  message: string;
  urlText?: string;
  url?: string;
}

export interface SuccessMessage {
  heading?: string;
  message: string;
  urlText?: string;
  url?: string;
}

export interface SelectOption {
  label: string;
  value: string | number;
}

export interface EventTargetValue {
  value: string | string[];
}

/**
 *   Enums
 * ------------------------------------------------------ */
export enum Fallback {
  IMAGE = '/image_default.jpg',
}

export enum TeamImportPartnerName {
  aes = 'AES',
  pg = 'Perfect Game',
  teamsnap = 'TeamSnap',
  usssa = 'USSSA',
}

export enum TeamImportPartnerLogo {
  aes = '/logos/partners/aes.svg',
  pg = '/logos/partners/perfect-game.svg',
  teamsnap = '/logos/partners/team-snap.svg',
  usssa = '/logos/partners/usssa.svg',
}

export enum OnboardingSteps {
  SPORTS_SELECTION = 'sports_selection',
  CREATE_TEAM = 'create_team',
  IMPORT_TEAM = 'import_team',
  ABOUT_TEAM = 'about_team',
  COMPLETE_ROSTER = 'complete_roster',
  ROSTER = 'roster',
}

export enum OnboardingStepsTracking {
  EXIT_ONBOARDING = 'Exit Onboarding',
  SPORTS_SELECTION = 'Sports Selection',
  CREATE_TEAM = 'Create Team',
  ABOUT_TEAM = 'Team Information',
  COMPLETE_ROSTER = 'Roster',
}

export enum LiveStreamSetUpSteps {
  INTRO_PAGE = 'intro_page',
  NEXT_GAME_SELECTION = 'next_game_selection',
  SCORE_GAME_SELECTION = 'score_game_selection',
  INVITE_TEAM_MANAGERS = 'invite_team_managers',
  ROSTER_SELECTION = 'roster_selection',
  STREAMING_SELECTION = 'streaming_selection',
}

export enum LiveStreamSetUpStepsTracking {
  INTRO_PAGE = 'Intro Page',
  NEXT_GAME_SELECTION = 'Next Game',
  SCORE_GAME_SELECTION = 'How Will You Score',
  INVITE_TEAM_MANAGERS = 'Invite Team Managers',
  ROSTER_SELECTION = "Who's Playing",
  STREAMING_SELECTION = "You're Ready to Go Live",
}

export enum SportsTypeName {
  basketball = 'Basketball',
  baseball = 'Baseball',
  hockey = 'Hockey',
  lacrosse = 'Lacrosse',
  soccer = 'Soccer',
  softball = 'Softball',
  volleyball = 'Volleyball',
  water_polo = 'Water Polo',
}

export enum AgeGroupOption {
  '6U' = '6U',
  '7U' = '7U',
  '8U' = '8U',
  '9U' = '9U',
  '10U' = '10U',
  '11U' = '11U',
  '12U' = '12U',
  '13U' = '13U',
  '14U' = '14U',
  '15U' = '15U',
  '16U' = '16U',
  '17U' = '17U',
  '18U' = '18U',
  'Adults' = 'Adults',
  'College' = 'College',
  'High School' = 'High School',
}

export interface GameDetails {
  date: string;
  time: string;
  scoreOpponentName: string;
  scheduleTsStart: number;
  teamId: string;
  sportType: string;
  sanctioningPartner?: string | number;
}

export interface GameResponse {
  id: string;
  createdBy: string;
  createdBecause: string;
  createdTs: number;
  modifiedUser: string;
  modifiedTs: number;
  DNOR_teamId: string;
  scoreGameId: string;
  scoreGameState: number;
  scoreInningNum: number;
  scoreOpponentName: string;
  scoreOurLineup: Array<unknown>;
  scoreWeAreHome: boolean | null;
  scoresAwayHome: unknown | null;
}

export enum StaffRole {
  Coach = 'coach',
  Manager = 'manager',
}

export interface StaffMember {
  email?: string;
  isLivestreamer?: boolean;
  isPermittedToCreateTeam?: boolean;
  nameFirst?: string;
  nameLast?: string;
  phoneList?: string[];
  roles?: StaffRole[];
  _id?: string;
  pending?: boolean;
  inviteCode?: string;
  updateRoles?: boolean;
  teamId?: string;
}

export interface InviteBody {
  invitedUserPhone: string;
  invitedUserName: string;
  teamId: string;
  teamRole: StaffRole;
}

export enum StaffMode {
  EDIT = 'Edit',
  ADD = 'Add',
}

export interface StaffModalProps {
  mode: StaffMode;
  team: shd.Team;
  staff: StaffMember[];
  selectedStaffMember?: StaffMember | null;
  onClose: () => void;
  onInviteSent: (invteBody: InviteBody) => void;
  onRemoveStaff: (updatedStaffMember: StaffMember) => void;
  onUpdateStaff: (updatedStaffMemeber: StaffMember) => void;
  setToast?: (toast: { label: string; background: string }) => void;
}

export enum StaffLimit {
  Admin = 3,
  Coach = 2,
}

export interface SettingsDeleteAccountProps {
  userId: string | null;
}

export interface SettingsRevenueCatProps {}
